'use client';

import { useState, FormEvent, CSSProperties } from 'react';
import Icon from '@/components/ui/AppIcon';
import { trackCtaClick, trackConversion } from '@/lib/analytics';

const CONTACT_LINKS = [
  { label: 'GITHUB',   value: 'github.com/NiranjanLangade',   icon: 'CodeBracketIcon',     color: 'var(--cyber-cyan)',    href: 'https://github.com/NiranjanLangade' },
  { label: 'LINKEDIN', value: 'linkedin.com/in/niranjan45/',  icon: 'UserIcon',             color: 'var(--cyber-magenta)', href: 'https://www.linkedin.com/in/niranjan45/' },
  { label: 'EMAIL',    value: 'niranjanlangade@gmail.com',       icon: 'EnvelopeIcon',         color: 'var(--cyber-green)',   href: 'mailto:niranjanlangade@gmail.com' },
  { label: 'YOUTUBE',  value: '@Niranjan_Codes',            icon: 'ChatBubbleLeftIcon',   color: 'var(--cyber-yellow)',  href: '#' },
];

export default function ContactSection() {
  const [formState, setFormState] = useState({
    name: '', email: '', subject: '', message: '', website: '', startedAt: Date.now(),
  });
  const [submitted, setSubmitted] = useState(false);
  const [focused, setFocused]     = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formState),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.error || 'Failed to send message.');
      }

      setSubmitted(true);
      setFormState({ name: '', email: '', subject: '', message: '', website: '', startedAt: Date.now() });
      trackConversion('lead_submitted', 'contact_form');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Transmission failed. Try again.';
      setSubmitError(message);
      trackConversion('lead_submit_failed', 'contact_form');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = () => `
    w-full font-mono text-sm px-4 py-3 outline-none transition-all duration-200
    bg-transparent
  `;

  const inputStyle = (field: string): CSSProperties => ({
    background: focused === field ? 'rgba(0,212,255,0.04)' : 'rgba(10,15,20,0.8)',
    border: `1px solid ${focused === field ? 'rgba(0,212,255,0.5)' : 'rgba(0,212,255,0.15)'}`,
    color: '#C8D8E8',
    clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
    boxShadow: focused === field ? '0 0 12px rgba(0,212,255,0.1)' : 'none',
  });

  return (
    <section
      id="contact"
      className="relative py-24 px-6 md:px-12 overflow-hidden"
      style={{ background: 'var(--cyber-black)' }}
      aria-label="Contact section"
    >
      {/* Top accent */}
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, var(--cyber-cyan), var(--cyber-magenta), transparent)' }}
      />

      {/* Background glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 50% 60% at 50% 80%, rgba(0,212,255,0.04) 0%, transparent 70%)',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-16 text-center">
          <p className="font-mono text-xs mb-3" style={{ color: 'var(--cyber-cyan)', letterSpacing: '0.3em' }}>
            // MODULE: 06
          </p>
          <h2 className="font-mono font-black text-4xl md:text-6xl mb-4" style={{ color: '#FFFFFF', letterSpacing: '-0.02em' }}>
            OPEN<span className="text-glow-cyan">_CHANNEL</span>
          </h2>
          <p className="font-mono text-sm" style={{ color: 'rgba(200,216,232,0.5)', maxWidth: 480, margin: '0 auto' }}>
            TRANSMISSION_PROTOCOL: SECURE // RESPONSE_TIME: {'<'}24H
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* LEFT: Contact info */}
          <div className="space-y-8">
            {/* Availability panel */}
            <div
              className="cyber-panel p-6 hud-corner"
              style={{ border: '1px solid rgba(57,255,20,0.2)' }}
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="relative flex h-3 w-3">
                  <span className="status-online absolute inline-flex h-full w-full rounded-full" />
                </span>
                <span className="font-mono text-xs font-bold text-glow-green" style={{ letterSpacing: '0.2em' }}>
                  SYSTEM_ONLINE
                </span>
              </div>
              <p className="font-mono text-sm leading-relaxed" style={{ color: 'rgba(200,216,232,0.7)' }}>
                Currently accepting full-time and contract engagements. Available for remote collaboration globally, or on-site in Pune, Maharashtra, India.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                {['FULL-TIME', 'CONTRACT', 'CONSULTING', 'REMOTE'].map((tag) => (
                  <span
                    key={tag}
                    className="font-mono text-[9px] px-2 py-1"
                    style={{
                      background: 'rgba(57,255,20,0.06)',
                      border: '1px solid rgba(57,255,20,0.2)',
                      color: 'var(--cyber-green)',
                      letterSpacing: '0.15em',
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div
              className="cyber-panel p-5"
              style={{ border: '1px solid rgba(0,212,255,0.2)' }}
            >
              <p className="font-mono text-[10px] mb-3" style={{ color: 'var(--cyber-cyan)', letterSpacing: '0.18em' }}>
                PRIORITY_CONTACT
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="mailto:niranjanlangade@gmail.com"
                  onClick={() =>
                    trackCtaClick(
                      'hire_me_via_email_contact',
                      'contact_priority_panel',
                      'mailto:niranjanlangade@gmail.com',
                    )
                  }
                  className="cyber-btn cyber-btn-primary px-5 py-2 font-mono text-[10px]"
                >
                  HIRE_ME_VIA_EMAIL
                </a>
                <a
                  href="https://www.linkedin.com/in/niranjan45/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackCtaClick(
                      'message_on_linkedin_contact',
                      'contact_priority_panel',
                      'https://www.linkedin.com/in/niranjan45/',
                    )
                  }
                  className="cyber-btn cyber-btn-outline px-5 py-2 font-mono text-[10px]"
                >
                  MESSAGE_ON_LINKEDIN
                </a>
              </div>
            </div>

            {/* Contact links */}
            <div className="space-y-3">
              {CONTACT_LINKS.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="flex items-center gap-4 p-4 group transition-all duration-200"
                  style={{
                    background: 'rgba(10,15,20,0.8)',
                    border: `1px solid ${link.color}15`,
                    clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor = `${link.color}40`;
                    (e.currentTarget as HTMLElement).style.boxShadow = `0 0 12px ${link.color}10`;
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor = `${link.color}15`;
                    (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                  }}
                  onClick={() => trackCtaClick('contact_link_click', 'contact_links', link.href)}
                  aria-label={`Contact via ${link.label}`}
                >
                  <div
                    className="w-8 h-8 flex items-center justify-center shrink-0"
                    style={{
                      background: `${link.color}10`,
                      border: `1px solid ${link.color}30`,
                      clipPath: 'polygon(0 0, calc(100% - 5px) 0, 100% 5px, 100% 100%, 5px 100%, 0 calc(100% - 5px))',
                    }}
                  >
                    <Icon name={link.icon as any} size={14} style={{ color: link.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[9px] mb-0.5" style={{ color: link.color, letterSpacing: '0.2em' }}>
                      {link.label}
                    </div>
                    <div className="font-mono text-xs truncate" style={{ color: 'rgba(200,216,232,0.7)' }}>
                      {link.value}
                    </div>
                  </div>
                  <Icon
                    name="ArrowTopRightOnSquareIcon"
                    size={14}
                    className="shrink-0 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    style={{ color: link.color, opacity: 0.5 }}
                  />
                </a>
              ))}
            </div>
          </div>

          {/* RIGHT: Terminal form */}
          <div>
            <div className="terminal-win overflow-hidden" style={{ clipPath: 'polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 0 100%)' }}>
              {/* Terminal header */}
              <div
                className="flex items-center gap-2 px-4 py-3"
                style={{ borderBottom: '1px solid rgba(0,212,255,0.1)', background: 'rgba(0,212,255,0.03)' }}
              >
                <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'var(--cyber-green)' }} />
                <span className="font-mono text-[10px] ml-3" style={{ color: 'rgba(200,216,232,0.4)', letterSpacing: '0.15em' }}>
                  TRANSMISSION_TERMINAL.SH
                </span>
              </div>

              {submitted ? (
                <div className="p-8 text-center space-y-4">
                  <div className="font-mono text-4xl text-glow-green">âœ“</div>
                  <p className="font-mono text-sm text-glow-cyan">TRANSMISSION_SENT</p>
                  <p className="font-mono text-xs" style={{ color: 'rgba(200,216,232,0.5)' }}>
                    MESSAGE ENCRYPTED AND DELIVERED. RESPONSE IN {'<'}24H.
                  </p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setFormState({
                        name: '',
                        email: '',
                        subject: '',
                        message: '',
                        website: '',
                        startedAt: Date.now(),
                      });
                    }}
                    className="cyber-btn cyber-btn-outline px-6 py-2 font-mono text-xs mt-4"
                  >
                    NEW_TRANSMISSION
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                  <div>
                    <label className="sr-only" htmlFor="website-field">
                      Website (leave empty)
                    </label>
                    <input
                      id="website-field"
                      type="text"
                      value={formState.website}
                      onChange={(e) => setFormState(s => ({ ...s, website: e.target.value }))}
                      autoComplete="off"
                      tabIndex={-1}
                      className="hidden"
                      aria-hidden="true"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[9px] block mb-1.5" style={{ color: 'var(--cyber-cyan)', letterSpacing: '0.2em' }}>
                      OPERATOR_NAME *
                    </label>
                    <input
                      type="text"
                      required
                      disabled={isSubmitting}
                      value={formState.name}
                      onChange={(e) => setFormState(s => ({ ...s, name: e.target.value }))}
                      onFocus={() => setFocused('name')}
                      onBlur={() => setFocused(null)}
                      placeholder="ENTER_NAME"
                      className={inputClass()}
                      style={inputStyle('name')}
                      aria-label="Your name"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[9px] block mb-1.5" style={{ color: 'var(--cyber-cyan)', letterSpacing: '0.2em' }}>
                      UPLINK_ADDRESS *
                    </label>
                    <input
                      type="email"
                      required
                      disabled={isSubmitting}
                      value={formState.email}
                      onChange={(e) => setFormState(s => ({ ...s, email: e.target.value }))}
                      onFocus={() => setFocused('email')}
                      onBlur={() => setFocused(null)}
                      placeholder="EMAIL@DOMAIN.COM"
                      className={inputClass()}
                      style={inputStyle('email')}
                      aria-label="Your email address"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[9px] block mb-1.5" style={{ color: 'var(--cyber-cyan)', letterSpacing: '0.2em' }}>
                      MISSION_BRIEF *
                    </label>
                    <input
                      type="text"
                      required
                      disabled={isSubmitting}
                      value={formState.subject}
                      onChange={(e) => setFormState(s => ({ ...s, subject: e.target.value }))}
                      onFocus={() => setFocused('subject')}
                      onBlur={() => setFocused(null)}
                      placeholder="PROJECT_TYPE // INQUIRY"
                      className={inputClass()}
                      style={inputStyle('subject')}
                      aria-label="Message subject"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[9px] block mb-1.5" style={{ color: 'var(--cyber-cyan)', letterSpacing: '0.2em' }}>
                      ENCRYPTED_MESSAGE *
                    </label>
                    <textarea
                      required
                      rows={5}
                      disabled={isSubmitting}
                      value={formState.message}
                      onChange={(e) => setFormState(s => ({ ...s, message: e.target.value }))}
                      onFocus={() => setFocused('message')}
                      onBlur={() => setFocused(null)}
                      placeholder="DESCRIBE YOUR PROJECT OR INQUIRY..."
                      className={inputClass() + ' resize-none'}
                      style={inputStyle('message')}
                      aria-label="Your message"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    onClick={() => trackCtaClick('contact_form_submit', 'contact_form', '/api/contact')}
                    className="cyber-btn cyber-btn-primary w-full py-3 font-mono text-sm font-bold flex items-center justify-center gap-3"
                  >
                    <Icon name="PaperAirplaneIcon" size={16} />
                    {isSubmitting ? 'TRANSMITTING...' : 'TRANSMIT_MESSAGE'}
                  </button>
                  {submitError && (
                    <p className="font-mono text-[10px] text-center" style={{ color: 'var(--cyber-magenta)' }}>
                      {submitError}
                    </p>
                  )}
                  <p className="font-mono text-[9px] text-center" style={{ color: 'rgba(200,216,232,0.3)', letterSpacing: '0.1em' }}>
                    SECURE_CHANNEL // ALL DATA ENCRYPTED
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

